/**
 * What: Renders the supported Shopify micro-survey authoring form.
 * Transport / data-flow: Emits local draft changes and save requests to `MicroSurveysPage`, which POSTs through the Shopify Worker.
 *
 */
import { supportedMicroSurveyTriggers, supportedQuestionKinds, type MicroSurveyDraft } from './microSurveyDraft'
import {
  shopifyComponentCopy,
  formatMicroSurveyChoiceIconLabel,
  formatMicroSurveyChoiceInterpretationLabel,
  formatMicroSurveyChoiceInterpretationValueLabel,
} from '@testfeed/product-language'
import { useAppBridge } from '@shopify/app-bridge-react'
import { trackShopifyAction } from '../../shopifyUiTracking'

type MicroSurveyEditorProps = {
  draft: MicroSurveyDraft
  errors: string[]
  isSaving: boolean
  onChange: (draft: MicroSurveyDraft) => void
  onCancel: () => void
  onSave: () => void
}

const interpretationOptions = [
  { label: shopifyComponentCopy.microSurveysMicroSurveyEditor.purchaseBarrier001, value: 'purchase_barrier' },
  { label: shopifyComponentCopy.microSurveysMicroSurveyEditor.useCase002, value: 'use_case' },
  { label: shopifyComponentCopy.microSurveysMicroSurveyEditor.psychographicSegment003, value: 'psychographic_segment' },
  { label: shopifyComponentCopy.microSurveysMicroSurveyEditor.noInterpretation004, value: 'none' },
]

function updateDraftField<Key extends keyof MicroSurveyDraft>(
  draft: MicroSurveyDraft,
  key: Key,
  value: MicroSurveyDraft[Key],
) {
  return {
    ...draft,
    [key]: value,
  }
}

function updateChoice(draft: MicroSurveyDraft, index: number, updates: Partial<MicroSurveyDraft['choices'][number]>) {
  return {
    ...draft,
    choices: draft.choices.map((choice, choiceIndex) => {
      if (choiceIndex !== index) return choice
      return {
        ...choice,
        ...updates,
      }
    }),
  }
}

function addChoice(draft: MicroSurveyDraft) {
  const choiceNumber = draft.choices.length + 1
  return {
    ...draft,
    choices: [
      ...draft.choices,
      {
        id: `choice_${choiceNumber}`,
        label: '',
        interpretationType: 'none' as const,
        interpretationValue: '',
      },
    ],
  }
}

function removeChoice(draft: MicroSurveyDraft, index: number) {
  return {
    ...draft,
    choices: draft.choices.filter((_choice, choiceIndex) => choiceIndex !== index),
  }
}

function choiceLabel(index: number) {
  if (index === 0) return 'First choice'
  if (index === 1) return 'Second choice'
  return `Choice ${index + 1}`
}

/** Collects the first runtime-supported micro-survey fields without exposing unsupported storefront behavior. */
export function MicroSurveyEditor({ draft, errors, isSaving, onChange, onCancel, onSave }: MicroSurveyEditorProps) {
  const shopify = useAppBridge()
  const triggerOptions = supportedMicroSurveyTriggers.map((trigger) => ({
    label: trigger.label,
    value: trigger.type,
  }))
  const questionKindOptions = supportedQuestionKinds.map((kind) => ({
    label: kind.label,
    value: kind.value,
  }))
  const errorBanner =
    errors.length > 0 ? (
      <s-banner
        heading={shopifyComponentCopy.microSurveysMicroSurveyEditor.microSurveyCannotBeSaved005}
        tone="critical"
      >
        <s-stack direction="block" gap="small-100">
          {errors.map((error) => (
            <s-paragraph key={error}>{error}</s-paragraph>
          ))}
        </s-stack>
      </s-banner>
    ) : null

  return (
    <s-section>
      <s-stack direction="block" gap="base">
        <s-stack direction="block" gap="small-100">
          <s-heading>{shopifyComponentCopy.microSurveysMicroSurveyEditor.microSurveyBuilder006}</s-heading>
          <s-text color="subdued">
            {shopifyComponentCopy.microSurveysMicroSurveyEditor.authorOneLowFrictionStorefront007}
          </s-text>
        </s-stack>
        {errorBanner}
        <s-select
          label={shopifyComponentCopy.microSurveysMicroSurveyEditor.trigger008}
          value={draft.triggerType}
          disabled={isSaving}
          onInput={(event) =>
            onChange(
              updateDraftField(draft, 'triggerType', event.currentTarget.value as MicroSurveyDraft['triggerType']),
            )
          }
        >
          {triggerOptions.map((option) => (
            <s-option key={option.value} value={option.value}>
              {option.label}
            </s-option>
          ))}
        </s-select>
        <s-select
          label={shopifyComponentCopy.microSurveysMicroSurveyEditor.questionType009}
          value={draft.questionKind}
          disabled={isSaving}
          onInput={(event) =>
            onChange(
              updateDraftField(draft, 'questionKind', event.currentTarget.value as MicroSurveyDraft['questionKind']),
            )
          }
        >
          {questionKindOptions.map((option) => (
            <s-option key={option.value} value={option.value}>
              {option.label}
            </s-option>
          ))}
        </s-select>
        <s-text-field
          label={shopifyComponentCopy.microSurveysMicroSurveyEditor.question010}
          value={draft.questionText}
          disabled={isSaving}
          autocomplete="off"
          onInput={(event) => onChange(updateDraftField(draft, 'questionText', event.currentTarget.value))}
        />
        <s-stack direction="block" gap="small-300">
          <s-heading>{shopifyComponentCopy.microSurveysMicroSurveyEditor.choices011}</s-heading>
          {draft.choices.map((choice, index) => {
            const canRemove = draft.choices.length > 2
            const removeButton = canRemove ? (
              <s-button
                disabled={isSaving}
                onClick={() => trackShopifyAction(shopify, 'remove-choice', () => onChange(removeChoice(draft, index)))}
              >
                {shopifyComponentCopy.microSurveysMicroSurveyEditor.remove012}
              </s-button>
            ) : null

            return (
              <s-stack direction="block" gap="small-200" key={`${choice.id}-${index}`}>
                <s-text-field
                  label={choiceLabel(index)}
                  value={choice.label}
                  disabled={isSaving}
                  autocomplete="off"
                  onInput={(event) =>
                    onChange(
                      updateChoice(draft, index, { label: event.currentTarget.value, id: event.currentTarget.value }),
                    )
                  }
                />
                <s-text-field
                  label={formatMicroSurveyChoiceIconLabel(choiceLabel(index))}
                  value={choice.icon ?? ''}
                  disabled={isSaving || draft.questionKind !== 'visual_choice'}
                  autocomplete="off"
                  onInput={(event) => onChange(updateChoice(draft, index, { icon: event.currentTarget.value }))}
                />
                <s-grid gridTemplateColumns="repeat(2, minmax(0, 1fr))" gap="base">
                  <s-select
                    label={formatMicroSurveyChoiceInterpretationLabel(choiceLabel(index))}
                    value={choice.interpretationType}
                    disabled={isSaving}
                    onInput={(event) =>
                      onChange(
                        updateChoice(draft, index, {
                          interpretationType: event.currentTarget
                            .value as MicroSurveyDraft['choices'][number]['interpretationType'],
                        }),
                      )
                    }
                  >
                    {interpretationOptions.map((option) => (
                      <s-option key={option.value} value={option.value}>
                        {option.label}
                      </s-option>
                    ))}
                  </s-select>
                  <s-text-field
                    label={formatMicroSurveyChoiceInterpretationValueLabel(choiceLabel(index))}
                    value={choice.interpretationValue}
                    disabled={isSaving || choice.interpretationType === 'none'}
                    autocomplete="off"
                    onInput={(event) =>
                      onChange(updateChoice(draft, index, { interpretationValue: event.currentTarget.value }))
                    }
                  />
                </s-grid>
                {removeButton}
              </s-stack>
            )
          })}
          <s-stack direction="inline">
            <s-button
              disabled={isSaving}
              onClick={() => trackShopifyAction(shopify, 'add-choice', () => onChange(addChoice(draft)))}
            >
              {shopifyComponentCopy.microSurveysMicroSurveyEditor.addChoice013}
            </s-button>
          </s-stack>
        </s-stack>
        <s-stack direction="inline" gap="small-300">
          <s-button variant="primary" loading={isSaving} disabled={isSaving} onClick={onSave}>
            {shopifyComponentCopy.microSurveysMicroSurveyEditor.saveMicroSurvey014}
          </s-button>
          <s-button disabled={isSaving} onClick={onCancel}>
            {shopifyComponentCopy.microSurveysMicroSurveyEditor.cancel015}
          </s-button>
        </s-stack>
      </s-stack>
    </s-section>
  )
}
